/*
 * ==============================
 * McpServerAddEditOverlay.jsx - Inline form for adding or editing MCP Servers.
 * Renders inside the NextMessageOptionsModal right panel.
 * ==============================
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Save, AlertTriangle } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// --- Form Components & Utilities ---
const InputField = React.memo(({ label, id, value, onChange, type = "text", placeholder, required = false, error, disabled = false, name, subText, onBlur }) => {
    const [localValue, setLocalValue] = useState(value || '');
    const debounceTimerRef = React.useRef(null);
  
    useEffect(() => { setLocalValue(value || ''); }, [value]);
  
    const handleChange = (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onChange({ target: { name: name || id, value: newValue, type: type } });
      }, 300);
    };
  
    useEffect(() => () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); }, []);
  
    return (
      <div className="mb-3">
        <label htmlFor={id} className="block text-xs font-medium text-on-surface-dim mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={type} id={id} name={name || id} value={localValue} onChange={handleChange}
          placeholder={placeholder} disabled={disabled} onBlur={onBlur}
          className={`w-full text-xs bg-surface-dark/50 border ${error ? 'border-red-500' : disabled ? 'border-white/10 bg-white/5 cursor-not-allowed' : 'border-white/10'} rounded-md p-1.5 focus:ring-1 focus:ring-primary focus:border-primary outline-none placeholder-on-surface-dim/60`}
        />
        {error && <p className="text-red-500 text-[10px] mt-0.5">{error}</p>}
        {subText && !error && <p className="text-on-surface-dim/70 text-[10px] mt-0.5">{subText}</p>}
      </div>
    );
});
  
const ToggleSwitch = React.memo(({ label, id, name, checked, onChange, subLabel, disabled = false }) => {
    const internalId = id || uuidv4();
    const handleChange = (e) => {
        if (onChange) {
            onChange({ target: { name: name || internalId, checked: e.target.checked, type: 'checkbox' } });
        }
    };
    return (
        <div className="mb-3 flex items-center">
            <label htmlFor={internalId} className="flex items-center cursor-pointer">
                <div className="relative">
                    <input type="checkbox" id={internalId} name={name || internalId} className="sr-only" checked={checked} onChange={handleChange} disabled={disabled} />
                    <div className={`block w-10 h-5 rounded-full transition-colors ${disabled ? 'opacity-60' : ''} ${checked ? 'bg-primary' : 'bg-surface-dark/50 border border-white/20'}`}></div>
                    <div className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${checked ? 'translate-x-5' : ''}`}></div>
                </div>
                <span className={`ml-3 text-xs font-medium ${disabled ? 'text-on-surface-dim/60' : 'text-on-surface-dim'}`}>{label}</span>
            </label>
            {subLabel && <p className={`ml-2 text-[10px] ${disabled ? 'text-on-surface-dim/50' : 'text-on-surface-dim/70'}`}>{subLabel}</p>}
        </div>
    );
});

const DEFAULT_SERVER_FORM = {
    id: '', name: '', callableToolId: '', url: '', apiKey: '',
    requiresAuth: false, token: '', description: '', enabled: true
};

const generateCallableToolId = (name) => {
    if (!name || typeof name !== 'string') return '';
    const suffix = '_MCP_Server';
    const maxBaseNameLength = 64 - suffix.length;
    let baseName = name.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').replace(/_{2,}/g, '_').replace(/^_+|_+$/g, '');
    if (baseName.length > maxBaseNameLength) {
        baseName = baseName.substring(0, maxBaseNameLength).replace(/_+$/g, '');
    }
    return baseName ? `${baseName}${suffix}` : '';
};

const McpServerAddEditOverlay = ({ 
    isOpen, 
    onClose, 
    onServerCreated, 
    onServerUpdated,
    existingServers, 
    addMcpServer,
    updateMcpServer,
    serverToEdit
}) => {
    const isEditMode = !!serverToEdit;
    const [editingServer, setEditingServer] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [touchedFields, setTouchedFields] = useState({});

    useEffect(() => {
        if (isOpen) {
            const initialState = isEditMode 
                ? { ...DEFAULT_SERVER_FORM, ...serverToEdit } 
                : { ...DEFAULT_SERVER_FORM, id: uuidv4(), callableToolId: '' };
            setEditingServer(initialState);
            setFormErrors({});
            setIsSaving(false);
            setTouchedFields({});
        } else {
            setEditingServer(null);
        }
    }, [isOpen, serverToEdit, isEditMode]);

    const validationSchema = useMemo(() => {
        if (!editingServer) return {};
        const errors = {};
        if (!editingServer.name?.trim()) {
            errors.name = "Server name is required.";
        } else {
            const generatedToolId = generateCallableToolId(editingServer.name);
            if ((existingServers || []).some(s => s.callableToolId === generatedToolId && s.id !== editingServer.id)) {
                errors.name = "A server with this name (or a similar one) already exists.";
            }
        }
        if (!editingServer.url?.trim()) {
            errors.url = "Server URL is required.";
        } else {
            try { new URL(editingServer.url); } catch (_) { errors.url = "Invalid URL format."; }
        }
        if (editingServer.requiresAuth && !editingServer.apiKey?.trim() && !editingServer.token?.trim()) {
            errors.apiKey = "API Key or Token is required if auth is enabled.";
        }
        return errors;
    }, [editingServer, existingServers]);

    useEffect(() => { setFormErrors(validationSchema); }, [validationSchema]);

    const isFormValid = Object.keys(validationSchema).length === 0;

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setEditingServer(prev => {
            if (!prev) return prev;
            return { ...prev, [name]: type === 'checkbox' ? checked : value };
        });
    }, []);

    const handleBlur = useCallback((e) => {
        const { name } = e.target;
        setTouchedFields(prev => ({ ...prev, [name]: true }));
    }, []);

    const handleSave = useCallback(async () => {
        setTouchedFields({ name: true, url: true, apiKey: editingServer?.requiresAuth });
        if (!isFormValid || !editingServer) return;
        
        setIsSaving(true);
        
        const serverToSave = { 
            ...editingServer, 
            callableToolId: generateCallableToolId(editingServer.name)
        };
        
        if (serverToSave.apiKey === '') delete serverToSave.apiKey;
        if (serverToSave.token === '') delete serverToSave.token;

        try {
            if (isEditMode) {
                if (!updateMcpServer) throw new Error("updateMcpServer function not provided.");
                await updateMcpServer(serverToSave.id, serverToSave);
                if (onServerUpdated) onServerUpdated(serverToSave);
            } else {
                if (!addMcpServer) throw new Error("addMcpServer function not provided.");
                const savedServerData = await addMcpServer(serverToSave);
                if (onServerCreated) onServerCreated(savedServerData);
            }
        } catch (error) {
            console.error(`Save server error (${isEditMode ? 'Edit' : 'Add'} Overlay):`, error);
            setFormErrors(prev => ({ ...prev, general: "An unexpected error occurred." }));
        } finally {
            setIsSaving(false);
        }
    }, [isFormValid, editingServer, addMcpServer, updateMcpServer, onServerCreated, onServerUpdated, isEditMode]);

    if (!isOpen || !editingServer) return null;

    const title = isEditMode ? "Edit MCP Server" : "Add New MCP Server";

    return (
        <motion.div 
            className="absolute inset-0 z-20 bg-surface-dark backdrop-blur-sm rounded-lg flex flex-col p-3 shadow-glass border border-[var(--border-color)]"
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: 10 }} 
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <h3 className="text-sm font-medium text-on-surface">{title}</h3>
                <button onClick={onClose} disabled={isSaving} className="p-1 rounded-full hover:bg-surface-dark/50 text-on-surface-dim disabled:opacity-50"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-1 pb-2 pl-0.5">
                <InputField 
                    label="Server Name" name="name" value={editingServer.name} onChange={handleInputChange} onBlur={handleBlur}
                    placeholder="My Custom API" required error={touchedFields.name ? formErrors.name : undefined} disabled={isSaving}
                    subText={`Callable ID: ${generateCallableToolId(editingServer.name) || '(will be generated)'}`}
                />
                <InputField 
                    label="Server URL" name="url" value={editingServer.url} onChange={handleInputChange} onBlur={handleBlur}
                    placeholder="https://api.example.com/v1" required error={touchedFields.url ? formErrors.url : undefined} disabled={isSaving} 
                />
                <InputField 
                    label="Description" name="description" value={editingServer.description} onChange={handleInputChange} onBlur={handleBlur}
                    placeholder="Optional: What this server does" disabled={isSaving} 
                />
                <ToggleSwitch 
                    label="Requires Authentication" name="requiresAuth" checked={editingServer.requiresAuth} onChange={handleInputChange}
                    subLabel="Needs an API key or Bearer token." disabled={isSaving} 
                />
                
                {editingServer.requiresAuth && (
                    <motion.div initial={{opacity:0, height:0}} animate={{opacity:1, height:'auto'}} className="pl-4 border-l-2 border-primary/20 mt-2 mb-2 space-y-3 overflow-hidden">
                        <InputField 
                            label="API Key" name="apiKey" type="password" value={editingServer.apiKey} onChange={handleInputChange} onBlur={handleBlur}
                            placeholder="Enter API Key (if required)" error={touchedFields.apiKey ? formErrors.apiKey : undefined} disabled={isSaving} 
                            subText="Leave blank if using only Bearer Token"
                        />
                        <InputField 
                            label="Bearer Token" name="token" type="password" value={editingServer.token} onChange={handleInputChange} onBlur={handleBlur}
                            placeholder="Enter Bearer Token (if required)" disabled={isSaving} subText="Leave blank if using only API Key"
                        />
                    </motion.div>
                )}
                {formErrors.general && <p className="text-red-500 text-xs mt-1 py-1.5 px-2 bg-red-500/10 rounded flex items-center gap-1.5"><AlertTriangle size={14}/>{formErrors.general}</p>}
            </div>

            <div className="mt-auto pt-2 border-t border-[var(--border-color)] flex justify-end gap-2 flex-shrink-0">
                <button onClick={onClose} disabled={isSaving} className="text-xs py-1.5 px-3 rounded-md hover:bg-white/10 text-on-surface-dim hover:text-on-surface transition-colors disabled:opacity-50">Cancel</button>
                <button onClick={handleSave} disabled={isSaving || !isFormValid} className="bg-primary text-on-primary text-xs font-medium py-1.5 px-4 rounded-md hover:bg-primary/80 disabled:opacity-50 disabled:bg-primary/40 flex items-center gap-1.5">
                    <Save size={14} /> {isSaving ? 'Saving...' : (isEditMode ? 'Update Server' : 'Save Server')}
                </button>
            </div>
        </motion.div>
    );
};

export default McpServerAddEditOverlay;